import React from 'react';
import { TCategory } from '../model';
import Radio from 'Components/DesignSystem/Radio/Radio';

type CategoryListItemProps = {
    id: string;
    item: TCategory;
    isSelected: boolean;
    onToggle: () => void;
    /**
     * Shared across the list so the browser treats these as ONE group — that is what
     * gives the set a single tab stop and arrow-key navigation. Without it each radio
     * is independent and only `checked` keeps them exclusive.
     */
    name?: string;
}

/**
 * One selectable category in the Category drawer.
 *
 * Was an **unstyled native `<input type="radio">`** — the browser's own blue, not the
 * design system's accent — inside a `<div role="button" onClick>`. Three things wrong
 * with that beyond the colour:
 *
 * - **No `name`.** The radios were not a group, so a keyboard user tabbed through every
 *   one of them and arrow keys did nothing.
 * - **Nested interactive controls.** Clicking the radio fired its own `onChange` AND the
 *   wrapping div's `onClick`, so `onToggle` ran twice. Invisible here only because
 *   selecting the same category twice is idempotent.
 * - **`role="button"` on a radio**, and the name text in a `<span>` rather than a label.
 *
 * DS Radio puts the input inside its own `<label>`, which fixes the association and the
 * double-fire together.
 */
const CategoryListItem = ({ id, item, isSelected, onToggle, name }: CategoryListItemProps) => (
    <Radio
        id={id}
        data-testid="components-categorylistitem-input-1"
        name={name}
        value={item?.id ?? ''}
        label={item?.name ?? ''}
        checked={isSelected}
        onChange={onToggle}
        className="tw-mb-2"
    />
);

export default CategoryListItem;
